import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { RichTextDisplay } from 'app/agriculture/components/RichTextDisplay/RichTextDisplay';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { useNote } from 'app/agriculture/queries/notes/useNote';
import { LinkIcon, NoteIcon } from 'app/agriculture/icons';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useGenericEntityFromId } from 'app/agriculture/queries/entities/useGenericEntityFromId';
import { LinkedEntityList } from 'app/agriculture/components/LinkedEntityList/LinkedEntityList';
import { ShowOnMapButton } from 'app/agriculture/components/ContentBox/components/ShowOnMapButton';
import { useUsers } from 'app/agriculture/hooks/users/useUsers';
import { convertMapLocationToPosition } from 'app/agriculture/components/utils/convertMapLocationToPosition';

interface NoteDetailsProps {
    noteId: string;
}

export function NoteDetailsComponent({ noteId }: NoteDetailsProps) {
    const { parentId, creatorId, createdAt, text, location } = useNote(noteId);
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const getParentEntity = useGenericEntityFromId();
    const parent = getParentEntity(parentId);
    const creator = useUsers().find((user) => user.id === creatorId);

    return (
        <ContentBox
            title="Note Details"
            Icon={NoteIcon}
            showOnMapButton={<ShowOnMapButton position={convertMapLocationToPosition(location)} />}
            variant="lg"
        >
            <ContentBox.Section scrollable>
                <ContentBox.Section>
                    <SimpleGrid columns={3}>
                        <StatDisplay
                            id="note-author"
                            name="Author"
                            value={creator?.name ?? 'Unknown'}
                        />
                        <StatDisplay
                            id="note-timestamp"
                            name="Created"
                            value={
                                <span title={createdAt.toLocaleString()}>
                                    {formatDistanceToNow(createdAt, { addSuffix: true })}
                                </span>
                            }
                        />
                        {!parent ? null : (
                            <StatDisplay
                                id="note-attached-to"
                                name="Location"
                                value={
                                    <LinkedEntityList entityIds={[parent.id]} icon={<LinkIcon />} />
                                }
                            />
                        )}
                    </SimpleGrid>
                </ContentBox.Section>
                <ContentBox.Section>
                    <h5>Note</h5>
                    <RichTextDisplay text={text} />
                </ContentBox.Section>
                <Actions
                    sticky
                    primaryAction={{
                        text: 'Edit',
                        startIcon: 'edit',
                        onClick: () => {
                            navigate('edit');
                        },
                    }}
                    backAction={{
                        startIcon: 'back',
                        text: 'All Notes',
                        onClick: () => {
                            routeActions.openList('Note');
                        },
                    }}
                />
            </ContentBox.Section>
        </ContentBox>
    );
}
